<script setup lang="ts">
import { ref } from 'vue';

import Dropdown, { type DropdownItems } from '@/components/Dropdown.vue';

import Counter from '@/components/Counter.vue';
import DateTime from '@/components/DateTime.vue';

import { useFilters } from '.';
import { allowedRanges } from './dates';

const { filters, filter } = useFilters();

const options = ref<DropdownItems[]>([
  { name: '7 nachten', value: '7' },
  { name: '8 nachten', value: '8' },
  { name: '9 nachten', value: '9' },
  { name: '10 nachten', value: '10' },
]);
</script>

<template>
  <ul>
    <li>
      <label for="date">Vertrekdatum</label>
      <DateTime id="date" v-model:date="filters.date" :allowedRanges="allowedRanges" />
    </li>
    <li>
      <label for="duration">Reisduur</label>
      <Dropdown inputClass="duration-class" id="duration" v-model:value="filters.duration" :options="options">
        <template #placeholder>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 10px">
            <g id="calendar-arrow-right 1">
              <path id="Vector" d="M16.5 3V6" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path id="Vector_2" d="M7.5 3V6" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path id="Vector_3" d="M9 13.5L15 13.5001" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path
                id="Vector_4"
                d="M12.5 16L15 13.5L12.5 11"
                stroke="black"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                id="Vector_5"
                d="M18 4.5H6C4.34315 4.5 3 5.84315 3 7.5V18C3 19.6569 4.34315 21 6 21H18C19.6569 21 21 19.6569 21 18V7.5C21 5.84315 19.6569 4.5 18 4.5Z"
                stroke="black"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </g>
          </svg>
          {{ filters.duration }} nachten
        </template>
      </Dropdown>
    </li>
    <li>
      <label for="people">Aantal personen</label>
      <Dropdown inputClass="people-class" id="people">
        <template #placeholder>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 10px">
            <g id="Users, Profile, Group 1" clip-path="url(#clip0_367_61)">
              <g id="Group">
                <path
                  id="Vector"
                  d="M8.00001 12C9.92748 12 11.49 10.4375 11.49 8.51002C11.49 6.58255 9.92748 5.02002 8.00001 5.02002C6.07254 5.02002 4.51001 6.58255 4.51001 8.51002C4.51001 10.4375 6.07254 12 8.00001 12Z"
                  stroke="black"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  id="Vector_2"
                  d="M17 12C18.3807 12 19.5 10.8807 19.5 9.5C19.5 8.11929 18.3807 7 17 7C15.6193 7 14.5 8.11929 14.5 9.5C14.5 10.8807 15.6193 12 17 12Z"
                  stroke="black"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  id="Vector_3"
                  d="M2 20V18.983C2 16.783 3.783 15 5.983 15H10.017C12.217 15 14 16.783 14 18.983V20"
                  stroke="black"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  id="Vector_4"
                  d="M17 15H18.102C20.302 15 22.085 16.783 22.085 18.983V20"
                  stroke="black"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
            </g>
            <defs>
              <clipPath id="clip0_367_61">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
          Volwassenen {{ filters.adults }}, kinderen {{ filters.children }}
        </template>
        <div class="dropdown__content">
          <Counter v-model:count="filters.adults" :min="1" :max="5">
            <template #title>
              <label>VOLWASSENEN</label>
              <p class="sub">{{ filters.adults }} personen</p>
            </template>
          </Counter>
          <hr />
          <Counter v-model:count="filters.children" :min="0" :max="5">
            <template #title>
              <label>KINDEREN</label>
              <p class="sub">{{ filters.children }} personen</p>
            </template>
          </Counter>
        </div>
        <p class="small">
          Eventuele kortingen voor kinderen of senioren worden berekend bij de laatste stap van de boeking, zodra de geboortedata zijn
          ingevoerd. Grote groep? Neem <a href="#">contact</a> op met ons.
        </p>
      </Dropdown>
    </li>
    <li>
      <label for="submit"></label>
      <button id="submit" type="submit" class="button" @click.prevent="filter">Zoeken</button>
    </li>
  </ul>
</template>

<style lang="scss">
.people-class,
.duration-class {
  background: var(--grey-dark, #eee) !important;

  color: #000;
  text-align: center;
  font-feature-settings:
    'clig' off,
    'liga' off;

  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 157%;
}

.dropdown__content {
  border-radius: 4px;
  border-bottom: 1px solid #dbdbdb;

  hr {
    margin: 0rem auto;
    width: 80%;
  }

  .sub {
    font-weight: 400;
    font-size: 13px;
    line-height: 16px;
    color: #656565;
  }
}

.small {
  font-size: 13px;
  border-radius: 4px;
  padding: 24px;
}
</style>
